import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/useI18n';
import { useTheme } from '../theme/ThemeProvider';
import { useRouter, Link } from '../router/Router';
import type { Route, RouteName } from '../router/routes';
import { USING_STUBS } from '../api';
import { StubBanner } from '../stubs/StubBanner';
import { IconChevronRight, IconMoon, IconSignOut, IconSun, Logo } from '../ui/icons';

/**
 * The frame both consoles are drawn in, and nothing more.
 *
 * It knows no roles and no sessions: what it shows in the sidebar arrives as
 * props from whichever console mounted it. That keeps the one component both
 * entry points share from being the place where the two get mixed up.
 */

export interface NavEntry {
  route: Route;
  label: string;
  icon: ReactNode;
  /** Route names that should light this entry up. */
  matches: RouteName[];
}

/**
 * One group of the menu. There are as many as the console has kinds of work:
 * the fleet and the account, or the tenancy and the account.
 */
export interface ShellNav {
  group: string;
  items: NavEntry[];
}

export function AppShell({
  scope,
  actor,
  nav,
  crumb,
  onSignOut,
  children,
}: {
  /** What this console is scoped to: all tenancies, or one tenancy. */
  scope: { name: string; sub: string };
  actor: { name: string; role: string };
  nav: ShellNav[];
  crumb: string;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { theme, toggle } = useTheme();
  const { route } = useRouter();

  // A keyboard lands at the top of the new page rather than back at the sidebar.
  useEffect(() => {
    document.getElementById('page-title')?.focus();
  }, [route]);

  return (
    <div className="app">
      <a className="skip-link" href="#main">
        {t('nav.skipToContent')}
      </a>

      <aside className="sidebar">
        <div className="sidebar-head">
          <Logo />
          <span className="brand-name">
            {t('common.brand')} <span className="soft">{t('common.console')}</span>
          </span>
        </div>

        <div className="scope-card">
          <span className="scope-logo" aria-hidden="true">
            {scope.name.slice(0, 1).toUpperCase()}
          </span>
          <div className="scope-meta">
            <div className="n">{scope.name}</div>
            <div className="p mono">{scope.sub}</div>
          </div>
        </div>

        <nav className="nav-scroll" aria-label={t('nav.primary')}>
          {nav.map((group) => (
            <div className="nav-group" key={group.group}>
              <div className="nav-label">{group.group}</div>
              {group.items.map((item) => (
                <Link
                  key={item.route.name}
                  to={item.route}
                  className="nav-item"
                  {...(item.matches.includes(route.name) ? { 'aria-current': 'page' as const } : {})}
                >
                  <span className="ic">{item.icon}</span>
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="user-chip">
            <span className="avatar" aria-hidden="true">
              {actor.name.slice(0, 1).toUpperCase()}
            </span>
            <div className="um">
              <div className="n">{actor.name}</div>
              <div className="r">{actor.role}</div>
            </div>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <nav className="crumbs" aria-label={t('shell.breadcrumb')}>
            <span className="c-root">{scope.name}</span>
            <span className="sep" aria-hidden="true">
              <IconChevronRight size={15} />
            </span>
            <span className="c-cur">{crumb}</span>
          </nav>

          <div className="topbar-right">
            <button
              type="button"
              className="icon-btn"
              onClick={toggle}
              aria-label={theme === 'dark' ? t('topbar.theme.toLight') : t('topbar.theme.toDark')}
            >
              {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </button>
            <button
              type="button"
              className="icon-btn"
              onClick={onSignOut}
              aria-label={t('common.signOut')}
            >
              <IconSignOut size={18} />
            </button>
          </div>
        </header>

        <main className="content" id="main">
          {USING_STUBS && (
            <div className="content-inner" style={{ marginBottom: 'var(--gap)' }}>
              <StubBanner />
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
